import React from 'react';
import { Users, Clock, CheckCircle, XCircle, Mail } from 'lucide-react';
import { SupplierCard } from './SupplierCard';

export const KanbanView = ({ 
  suppliers = [], 
  quotationItems = [], 
  selectedSupplierId,
  onSupplierSelect,
  onViewDocuments 
}) => {
  // Organizar suppliers por estado
  const organizeSuppliersByStatus = () => {
    const pending = [];
    const responded = [];
    const declined = [];

    suppliers.forEach(supplier => {
      const hasQuotationItems = quotationItems.some(item => item.supplier_id === supplier.id);
      
      if (hasQuotationItems) {
        responded.push(supplier);
      } else if (supplier.status === 'declined') {
        declined.push(supplier);
      } else {
        pending.push(supplier);
      }
    });

    return { pending, responded, declined };
  };

  const { pending, responded, declined } = organizeSuppliersByStatus();

  const columns = [
    {
      id: 'pending',
      title: 'Pending Response',
      icon: Clock,
      count: pending.length,
      suppliers: pending,
      color: 'amber',
      bgColor: 'bg-amber-50/50 dark:bg-amber-900/10',
      borderColor: 'border-amber-200 dark:border-amber-800',
      iconColor: 'text-amber-600 dark:text-amber-400'
    },
    {
      id: 'responded',
      title: 'Responded',
      icon: CheckCircle,
      count: responded.length,
      suppliers: responded,
      color: 'green',
      bgColor: 'bg-green-50/50 dark:bg-green-900/10',
      borderColor: 'border-green-200 dark:border-green-800',
      iconColor: 'text-green-600 dark:text-green-400'
    },
    {
      id: 'declined',
      title: 'Declined',
      icon: XCircle,
      count: declined.length,
      suppliers: declined,
      color: 'red',
      bgColor: 'bg-red-50/50 dark:bg-red-900/10',
      borderColor: 'border-red-200 dark:border-red-800',
      iconColor: 'text-red-600 dark:text-red-400'
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-blue-600 dark:text-blue-400" />
          <h3 className="text-lg font-semibold text-slate-800 dark:text-slate-200">
            Supplier Response Overview
          </h3>
        </div>
        <div className="text-sm text-slate-500 dark:text-slate-400">
          {suppliers.length} suppliers invited
        </div>
      </div>

      {/* Kanban Board */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {columns.map((column) => {
          const Icon = column.icon;
          
          return (
            <div
              key={column.id}
              className={`
                ${column.bgColor} ${column.borderColor}
                border-2 border-dashed rounded p-4 min-h-[400px]
                transition-all duration-200 hover:shadow-lg
              `}
            >
              {/* Column Header */}
              <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200/50 dark:border-slate-700/50">
                <div className="flex items-center space-x-2">
                  <Icon className={`h-5 w-5 ${column.iconColor}`} />
                  <h4 className="font-semibold text-slate-700 dark:text-slate-300">
                    {column.title}
                  </h4>
                </div>
                <div className={`
                  px-2 py-1 rounded-full text-xs font-medium
                  ${column.color === 'amber' ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' :
                    column.color === 'green' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' :
                    'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                  }
                `}>
                  {column.count}
                </div>
              </div>

              {/* Suppliers List */}
              <div className="space-y-3">
                {column.suppliers.length > 0 ? (
                  column.suppliers.map((supplier) => {
                    const hasQuotationItems = quotationItems.some(item => item.supplier_id === supplier.id);
                    
                    return (
                      <SupplierCard
                        key={supplier.id}
                        supplier={supplier}
                        hasQuotationItems={hasQuotationItems}
                        isSelected={selectedSupplierId === supplier.id}
                        onSelect={onSupplierSelect}
                        onViewDocuments={onViewDocuments}
                        className="mb-3"
                      />
                    );
                  })
                ) : (
                  <div className="text-center py-8">
                    <Icon className={`h-8 w-8 mx-auto mb-2 ${column.iconColor} opacity-50`} />
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      No suppliers in this category
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary Stats */}
      <div className="bg-white dark:bg-slate-800 rounded border border-slate-200 dark:border-slate-700 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-200">
              {suppliers.length}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Total Invited
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {responded.length}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Responded
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
              {pending.length}
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Pending
            </div>
          </div>
          
          <div className="text-center">
            <div className="text-2xl font-bold text-slate-600 dark:text-slate-400">
              {suppliers.length > 0 ? Math.round((responded.length / suppliers.length) * 100) : 0}%
            </div>
            <div className="text-sm text-slate-500 dark:text-slate-400">
              Response Rate
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}; 